import {
  TokenResponse,
  EncryptedLoginRequest,
  EncryptedPasswordChangeRequest,
  User,
} from "../types/auth";

import apiClient from "./api";

class AuthService {
  //@ts-ignore
  private keyPair: CryptoKeyPair | null = null;
  private serverPublicKey: CryptoKey | null = null;

  /**
   * Initialize encryption by generating client key pair and fetching server public key
   */
  async initialize(): Promise<void> {
    try {
      // Generate client RSA key pair
      this.keyPair = await window.crypto.subtle.generateKey(
        {
          name: "RSA-OAEP",
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: "SHA-256",
        },
        true,
        ["encrypt", "decrypt"],
      );

      // Fetch server's public key
      const response = await apiClient.get<{ public_key: string }>(
        "/auth/public-key",
      );

      this.serverPublicKey = await this.importPublicKey(response.public_key);
    } catch (error) {
      console.error("Error initializing auth service:", error);
      throw error;
    }
  }

  /**
   * Import public key from PEM format
   */
  private async importPublicKey(pemKey: string): Promise<CryptoKey> {
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";

    // Clean up PEM format
    const pemContents = pemKey
      .replace(pemHeader, "")
      .replace(pemFooter, "")
      .replace(/\\s/g, "");

    // Convert to binary
    const binaryKey = Uint8Array.from(atob(pemContents), (c) =>
      c.charCodeAt(0),
    );

    // Import as crypto key
    return await window.crypto.subtle.importKey(
      "spki",
      binaryKey,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["encrypt"],
    );
  }

  /**
   * Encrypt password with server's public key
   */
  async encryptPassword(password: string): Promise<string> {
    // Initialize if not already done
    if (!this.serverPublicKey) {
      await this.initialize();
    }

    // Ensure server public key is available
    if (!this.serverPublicKey) {
      throw new Error("Server public key is not initialized");
    }

    // Encrypt password
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const encrypted = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      this.serverPublicKey,
      data,
    );

    // Return as base64 string
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  /**
   * Login with encrypted password
   */
  async login(username: string, password: string): Promise<TokenResponse> {
    // Initialize if not already done
    if (!this.serverPublicKey) {
      await this.initialize();
    }

    // Encrypt password
    const encrypted_password = await this.encryptPassword(password);

    // Send login request
    const loginData: EncryptedLoginRequest = {
      username,
      encrypted_password,
    };

    return await apiClient.post<TokenResponse>(
      "/auth/login-encrypted",
      loginData,
    );
  }

  /**
   * Validate auth token
   */
  async validateToken(token: string): Promise<boolean> {
    try {
      // Make a request to get user profile, which will fail if token is invalid
      await apiClient.get("/auth/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get user profile
   */
  async getUserProfile(token?: string): Promise<User> {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined;

    return await apiClient.get<User>("/auth/profile", config);
  }

  /**
   * Change password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    // Initialize if not already done
    if (!this.serverPublicKey) {
      await this.initialize();
    }

    // Encrypt passwords
    const encrypted_current_password =
      await this.encryptPassword(currentPassword);
    const encrypted_new_password = await this.encryptPassword(newPassword);

    const data: EncryptedPasswordChangeRequest = {
      encrypted_current_password,
      encrypted_new_password,
    };

    await apiClient.post("/auth/change-password-encrypted", data);
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      // Even if server logout fails, clear local tokens
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      // Decode JWT payload
      const payload = JSON.parse(atob(token.split(".")[1]));

      // Check expiration time
      const currentTime = Math.floor(Date.now() / 1000);

      return payload.exp < currentTime;
    } catch (error) {
      console.error("Error checking token expiration:", error);

      return true; // Assume expired on error
    }
  }

  /**
   * Request password reset
   * Pending
   */
  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post("/auth/request-password-reset", { email });
  }
}

// Create singleton instance
export const authService = new AuthService();
